// Which tab is showing, kept in the URL.
//
// In the URL rather than in component state so that a link can point AT a tab:
// the Home reminders link straight to `?tab=competencies`, the needs-attention
// card jumps to the folder holding the record, and a manager can send somebody
// a link to the thing they are talking about rather than to a page plus
// instructions.
//
// The default tab is the bare URL, so the ordinary case has no query string at
// all. An unrecognised value falls back rather than rendering nothing — a stale
// bookmark from a renamed tab should land somewhere sensible.
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export function useTabParam(
  keys: readonly string[],
  fallback: string = keys[0] ?? ''
): [string, (key: string) => void] {
  const [params, setParams] = useSearchParams();

  const asked = params.get('tab');
  const active = asked !== null && keys.includes(asked) ? asked : fallback;

  const setActive = useCallback(
    (key: string) => {
      const next = new URLSearchParams(params);
      if (key === fallback) next.delete('tab');
      else next.set('tab', key);
      // `replace`, so paging through the tabs does not fill the back button
      // with steps the reader has to walk out of one at a time.
      setParams(next, { replace: true });
    },
    [params, setParams, fallback]
  );

  return [active, setActive];
}
